import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpService } from './http.service';

// Note: preTeaching, warmup, and wrapUp were early iterations, removed from the data model (SLPV-743).
export const SECTION_KEYS = ['opener', 'coreMaterial', 'practice', 'extend'];

export const SECTION_LABELS: Record<string, string> = {
  opener: 'Session Opener',
  coreMaterial: 'Core Material',
  practice: 'Practice',
  extend: 'Extend',
};

export const SECTION_ICONS: Record<string, string> = {
  opener: '📖',
  coreMaterial: '▶️',
  practice: '🗣️',
  extend: '✨',
};

@Injectable({
  providedIn: 'root',
})
export class CurriculumSessionService {
  constructor(private http: HttpService) {}

  list(params: any = {}): Observable<any> {
    const apiParams: any = { limit: params.limit || 50 };
    if (params.page) {
      apiParams.page = params.page;
    }
    if (params.unitId) {
      apiParams.unitId = params.unitId;
    }

    return this.http.get('admin/curriculum-sessions', apiParams).pipe(
      map(response => {
        let sessions: any[] = response.data || [];

        // Client-side filtering until API supports these params
        if (params.clinicalApproved === true) {
          sessions = sessions.filter((s: any) => s.clinicalApproved === true);
        } else if (params.clinicalApproved === false) {
          sessions = sessions.filter((s: any) => s.clinicalApproved === false);
        }
        if (params.text) {
          const term = params.text.toLowerCase();
          sessions = sessions.filter((s: any) => s.unitTitle?.toLowerCase().includes(term));
        }

        return {
          sessions,
          total: sessions.length,
          totalPages: response.meta?.totalPages ?? 1,
          currentPage: response.meta?.page ?? 1,
        };
      })
    );
  }

  get(sessionId: string): Observable<any> {
    return this.http.get(`admin/curriculum-sessions/${sessionId}`).pipe(map(response => ({ session: response.data })));
  }

  getNextUnreviewed(afterSessionId?: string): Observable<any> {
    return this.http.get('admin/curriculum-sessions', { limit: 200 }).pipe(
      map(response => {
        const unreviewed = (response.data as any[]).filter((s: any) => !s.clinicalApproved);
        if (!afterSessionId) {
          return { session: unreviewed[0] || null, total: unreviewed.length };
        }
        const idx = unreviewed.findIndex((s: any) => s.sessionId === afterSessionId);
        const next = unreviewed[idx + 1] || null;
        return { session: next, total: unreviewed.length };
      })
    );
  }

  save(session: any): Observable<any> {
    const payload: any = {
      unitId: session.unitId,
      sessionOrder: session.sessionOrder,

      clinicalApproved: session.clinicalApproved,
      opener: session.opener || null,
      coreMaterial: session.coreMaterial || null,
      practice: session.practice || null,
      extend: session.extend || null,
      sessionNote: session.sessionNote ?? null,
      sessionNoteTip: session.sessionNoteTip ?? null,
    };

    if (session.sessionId) {
      return this.http.put(`admin/curriculum-sessions/${session.sessionId}`, payload).pipe(map(response => ({ session: response.data })));
    }
    return this.http.post('admin/curriculum-sessions', payload).pipe(map(response => ({ session: response.data })));
  }

  delete(sessionId: string): Observable<any> {
    return this.http.delete(`admin/curriculum-sessions/${sessionId}`);
  }
}
